import {
  BookingState, Gym, GymTypes, IIndividualLeaderboardData,
  IInnerTeamLeaderboardData,
  ILeaderboardInstance,
  Instructor,
  IUserLeaderboardInstance,
  IUserWorkoutAggregate,
  IUserWorkoutSessionMemberReport,
  IUserWorkoutSessionSummary,
  IWorkoutDates,
  LeaderboardData,
  NextWorkout,
  NotFoundError,
  propNamesIUserWorkoutSessionMemberReport,
  propNamesIUserWorkoutSessionSummary,
  RemovedReasonType,
} from 'fitbeat.models';
import moment from 'moment';
import { MangoSelector } from 'nano';
import { BookingsConfig, homeWorkoutTypes, LeaderboardConfig } from '../../../config';
import {
  IExternalReference,
  UserWorkoutSession, WorkoutSelection, WorkoutSettings,
} from '../../../entities';
import { Mapper } from '../../serialization/mapper';
import { BaseSerializedWorkoutRepository } from '../base-serialized-workout-repository';
import { DbGymRepository } from './db-gym-repository';
import { HOME_PERSONAL_TRAINERS } from './db-instructors-repository';
import { PouchDbQueryHelper } from './pouchdb-query-helper';

const MSEC_PER_MINUTE = 60 * 1000;

export class DbWorkoutRepository extends BaseSerializedWorkoutRepository {
  public static buildDates(session: IWorkoutDates) {
    session.earliestAllowedStartTime = new Date(session.earliestAllowedStartTime);
    session.gymCheckinTime = session.gymCheckinTime != null ? new Date(session.gymCheckinTime) : undefined;
    session.startTime = new Date(session.startTime);
    session.endTime = new Date(session.endTime);
    if (session.startTimeOfFirstExercise) {
      session.startTimeOfFirstExercise = new Date(session.startTimeOfFirstExercise);
    } else {
      session.startTimeOfFirstExercise = new Date(session.startTime);
    }
    if (session.createdAt) {
      session.createdAt = new Date(session.createdAt);
    }
    if (session.expiresAt) {
      session.expiresAt = new Date(session.expiresAt);
    }
    if (session.removedAt) {
      session.removedAt = new Date(session.removedAt);
    }
    if (session.originalEndTime) {
      session.originalEndTime = new Date(session.originalEndTime);
    }
  }

  private static convertToRawDocumentToSave(userWorkoutSession: UserWorkoutSession) {
    Mapper.makeSerializable(userWorkoutSession);

    const doc = JSON.parse(JSON.stringify(userWorkoutSession));
    doc._id = userWorkoutSession.uniqueReference;

    return doc;
  }

  private db: PouchDbQueryHelper;

  private maxSessionDuration: number = BookingsConfig.longBookingIntervalsInMinutes * MSEC_PER_MINUTE;

  constructor(private gymStateDb: any, private generalSettings: any, private logger: any) {
    super();
    this.db = new PouchDbQueryHelper(gymStateDb, 'DbWorkoutRepository', logger);
    if (!this.logger) {
      this.logger = {
        info: () => { /* no-op */ },
      };
    }
    if (!this.logger.startTimer) {
      this.logger.startTimer = () => ({
        done: () => { /* no-op */ },
      });
    }
  }

  public async getAll(gym: Gym): Promise<UserWorkoutSession[]> {
    const timeBefore = Date.now();
    const results: any = await this.gymStateDb.allDocs({ include_docs: true });
    this.logger.info(`getAll took ${Date.now() - timeBefore} to complete`);
    const docs: any[] = results
      .rows
      .map((row: any) => row.doc)
      .filter((doc: any) => doc._id !== '0' && doc.gymName === gym.name);
    const sessions: UserWorkoutSession[] = docs
      .map((doc: any): UserWorkoutSession => this.assignToUserWorkoutSessionObject(doc));

    Mapper.setupAllAfterDeserialize(sessions);
    return sessions;
  }

  public async getAllWorkoutsForSingleUser(
    userProfileId: string,
    includeRemovedWorkouts = false,
    limit = Number.MAX_SAFE_INTEGER,
    includeProvisional = true,
  ): Promise<UserWorkoutSession[]> {
    const start = this.logger.startTimer();
    const result = await this.gymStateDb.find({
      limit,
      selector: {
        userProfileId,
        ...(includeRemovedWorkouts ? {} : { removedAt: false }),
        ...(includeProvisional ? {} : { expiresAt: { $exists: false } }),
      },
      use_index: process.env.TESTS
        ? undefined
        : ['_design/userIdEndTimeAndRemovedAtIndex', 'userIdEndTimeAndRemovedAtIndex'],
      sort: process.env.TESTS
        ? undefined
        : [{ endTime: 'desc' }],
    });
    start.done(`loaded ${result.docs.length} workouts for ${userProfileId}`);
    return result.docs;
  }

  public async getUserWorkoutCount(uid: string): Promise<number> {
    const resultFromView = await this.db.queryView('total-workouts-stats/total-workouts', {
      startkey: ['TotalStartedWorkouts', uid],
      endkey: ['TotalStartedWorkouts', uid, {}],
      group_level: 2,
    });
    if (resultFromView.length > 0) {
      return resultFromView[0].value;
    }
    return 0;
  }

  public async getUserWorkoutAggregates(
    gymName: string,
    tz: string,
    workoutsDateRange?: { workoutsFromDate: Date, workoutsToDate?: Date },
  ):
    Promise<IUserWorkoutAggregate[]> {
    let aggregateResultFromView;

    if (workoutsDateRange) {
      const convertedFromDate = moment.tz(workoutsDateRange.workoutsFromDate, tz).toDate().toISOString();
      const convertedToDate = workoutsDateRange.workoutsToDate && moment.tz(
        workoutsDateRange.workoutsToDate,
        tz,
      ).toDate().toISOString();

      const data = await this.db.queryView('workoutSelectionForUser/dateRangeBusinessToolsWorkoutAggregate', {
        start_key: [gymName, convertedFromDate],
        end_key: convertedToDate ? [gymName, convertedToDate, {}] : [gymName, {}],
        group_level: 4,
      }) as [{
        key: string[],
        value: number,
      }];

      aggregateResultFromView = this.groupWorkoutAggregateData(data);
    } else {
      aggregateResultFromView = await this.db.queryView('workoutSelectionForUser/businessToolsWorkoutAggregate', {
        start_key: [gymName],
        end_key: [gymName, {}],
        group_level: 3,
      }) as [{
        key: string[],
        value: number,
      }];
    }

    const firstAndLastResultFromView = await this.db.queryView('workoutSelectionForUser/firstAndLastWorkouts', {
      group_level: 1,
    }) as [{
      key: string,
      value: {
        sum: number,
        count: number,
        min: number,
        max: number,
        sumsqr: number,
      },
    }];

    const mappedWorkouts = new Map<string, IUserWorkoutAggregate>();

    aggregateResultFromView.map((doc) => {
      const userId = doc.key[1];
      const existingData = mappedWorkouts.get(userId) || {
        userProfileId: userId,
        totalCompletedStudio: 0,
        totalMissedStudio: 0,
        totalMissedHome: 0,
        totalCompletedHome: 0,
        totalCompleted: 0,
        totalHRCompleted: 0,
        totalBurnCompleted: 0,
        totalBuildCompleted: 0,
        totalBalanceCompleted: 0,
        totalCancelledStudio: 0,
        totalCustomisedCompleted: 0,
        firstWorkout: 0,
        lastWorkout: 0,
        totalDurationInMinutes: 0,
        totalEffortPoints: 0,
        totalCalories: 0,
        totalStudio28mins: 0,
        totalStudio48mins: 0,
        totalHome17mins: 0,
        totalHome29mins: 0,
        totalHome42mins: 0,
      };
      existingData[doc.key[2]] = doc.value;
      const userFirstAndLast = firstAndLastResultFromView.find((item) => item.key === userId);
      if (userFirstAndLast) {
        existingData.firstWorkout = userFirstAndLast?.value.min;
        existingData.lastWorkout = userFirstAndLast?.value.max;
      }
      mappedWorkouts.set(userId, existingData);
    });
    return Array.from(mappedWorkouts.values());
  }

  public async getUserWorkoutCountForGym(uid: string, gymName: string): Promise<number> {
    const resultFromView = await this.db.queryView('total-workouts-stats/total-workouts', {
      startkey: ['TotalStartedWorkouts', uid, gymName],
      endkey: ['TotalStartedWorkouts', uid, gymName, {}],
    });
    let count = 0;
    if (resultFromView.length > 0) {
      count = resultFromView[0].value;
    }
    return count;
  }

  public async getAllInfluencerWorkoutsInDateRangeForGym(
    from: Date,
    to: Date,
    gymName: string,
    userProfileId: string,
  ): Promise<UserWorkoutSession[]> {
    const latestStartTime = new Date(from.getTime() - this.maxSessionDuration);

    const queryOptions = {
      selector: {
        startTime: {
          $gte: latestStartTime,
          $lte: to,
        },
        endTime: {
          $gte: from,
        },
        gymName,
        isStreamingWorkout: true,
        pairedSessionReference: {
          $exists: false,
        },
        removedReason: {
          $exists: false,
        },
        userProfileId: {
          $ne: userProfileId,
        },
      },
      limit: 1000,
      use_index: ['_design/startTimeOnlyIndex', 'startTimeOnlyIndex'],
    };

    const logParams = `from: ${from}, to: ${to}`;
    const resultDocs = await this.db.queryAndGetDocs(
      `getAllInfluencerWorkoutsInDateRangeForGym(${logParams})`,
      queryOptions,
    );

    const sessions = resultDocs.map((session: UserWorkoutSession) => this.assignToUserWorkoutSessionObject(session));
    Mapper.setupAllAfterDeserialize(sessions);
    return sessions;
  }

  public async getLastWorkoutWorkoutSelectionId(
    uid: string,
    settings: WorkoutSettings,
    gymType: GymTypes,
    shouldIncludeUnderscore = true,
  ): Promise<{
      startTime: Date,
      workoutSelectionIndex: number,
      workoutSelectionKey: string,
    } | undefined> {
    const settingsKey = WorkoutSelection.refSelectionString(settings, gymType, shouldIncludeUnderscore);
    const resultFromView = await this.db.queryView('workoutSelectionForUser/workoutSelectionForUser', {
      startkey: [uid, settingsKey, {}],
      endkey: [uid, settingsKey],
      limit: 1,
      descending: true,
    });

    const result = resultFromView[0];
    if (!result) {
      return undefined;
    }
    const workoutSelectionkeyindex = 1;
    const startTimeIndex = 2;
    return {
      startTime: new Date(result.key[startTimeIndex]),
      workoutSelectionKey: result.key[workoutSelectionkeyindex],
      workoutSelectionIndex: result.value,
    };
  }

  public async getAllWorkoutsForUsers(
    userProfileIds: string[],
    from: Date,
    to: Date,
    ignoreCheckIn = false,
  ): Promise<UserWorkoutSession[]> {
    const result = await this.gymStateDb.find({
      limit: Number.MAX_SAFE_INTEGER,
      selector: {
        endTime: {
          $gt: from,
          $lt: to,
        },
        userProfileId: {
          $in: userProfileIds,
        },
        removedAt: false,
        ...(ignoreCheckIn
          ? { }
          : { checkedIn: true }),
      },
      use_index: process.env.TESTS
        ? undefined
        : ['_design/userIdEndTimeAndRemovedAtIndex', 'userIdEndTimeAndRemovedAtIndex'],
      fields: [
        '_id',
        'startTime',
        'startTimeOfFirstExercise',
        'endTime',
        'additionalSecondsBeforeFirstExercise',
        'userProfileId',
        'uniqueReference',
        'performanceMetrics',
        'leaderboards',
      ],
      sort: [{ endTime: 'desc' }],
    });
    return result.docs;
  }

  public add(userWorkoutSession: UserWorkoutSession): Promise<void> {
    const doc = DbWorkoutRepository.convertToRawDocumentToSave(userWorkoutSession);
    return this.gymStateDb.put(doc);
  }

  public async addOrUpdate(userWorkoutSession: UserWorkoutSession): Promise<void> {
    // update already has upsert logic in
    await this.update(userWorkoutSession);
  }

  public async update(userWorkoutSession: UserWorkoutSession): Promise<void> {
    const oldSession = await this.getWorkoutSessionFromId(userWorkoutSession.uniqueReference);
    if (!oldSession) {
      return this.add(userWorkoutSession);
    }
    // TODO: this is bad: it force overrides the document, ignoring any possible conflicts with the db version
    this.logger.warn(`DbWorkoutRepository.update replaces documents, without rev checking ${JSON.stringify({
      id: userWorkoutSession.uniqueReference,
      oldSessionRev: (oldSession as any)._rev,
      newDocumentRev: (userWorkoutSession as any)._rev,
    })}`);
    (userWorkoutSession as any)._rev = (oldSession as any)._rev;
    (userWorkoutSession as any)._id = (oldSession as any)._id;
    return this.add(userWorkoutSession);
  }

  public async bulkUpdate(userWorkoutSessions: UserWorkoutSession[]): Promise<UserWorkoutSession[]> {
    const docs = userWorkoutSessions.map(DbWorkoutRepository.convertToRawDocumentToSave);
    this.logger.info(`bulk updating ${docs.length} workouts`);
    const result: { error?: boolean }[] = await this.gymStateDb.bulkDocs(docs);
    this.logger.info(`bulk updated workouts ${JSON.stringify(result)}`);
    return result
      .map((docResult, index) => {
        if (docResult.error) {
          return userWorkoutSessions[index];
        }
        return undefined;
      })
      .filter((session: UserWorkoutSession | undefined): session is UserWorkoutSession => session !== undefined);
  }

  public async updateWorkoutInstructorSetting(uniqueReference: string, instructorId: string): Promise<void> {
    try {
      const oldSession = await this.getWorkoutSessionFromId(uniqueReference);
      if (!oldSession) {
        throw NotFoundError.Workout(uniqueReference);
      }
      const settings = {
        ...oldSession.settings,
        homeWorkoutInstructor: instructorId,
      };
      oldSession.settings = settings;
      return this.gymStateDb.put(oldSession);
    } catch (error) {
      this.logger.warn(`Error occurred in update instructor on workout settings: ${error.message}`);
    }
  }

  public async updateUserWorkoutsGymName(
    userProfileId: string,
    memberNewGym: string,
    replaceGym?: {
      previousGym: string,
      newGym: string,
    },
  ): Promise<void> {
    const workouts = await this.getAllWorkoutsForSingleUser(userProfileId, true);
    this.logger.info(`found ${workouts.length} workout session to update for user: ${userProfileId}`);
    for (const workout of workouts) {
      try {
        this.logger.info(`updating workout session ${workout.uniqueReference}`);
        workout.memberMainGym = memberNewGym;

        if (replaceGym && workout.gymName === replaceGym.previousGym) {
          workout.gymName = replaceGym.newGym;
        }

        await this.gymStateDb.put(workout);
      } catch (error) {
        this.logger.error(`failed to update workout session: ${workout.uniqueReference}, ${error.message}`);
      }
    }
  }

  public async updateWorkoutSettings(uniqueReference: string, settings: WorkoutSettings): Promise<void> {
    try {
      const oldSession = await this.getWorkoutSessionFromId(uniqueReference);
      if (!oldSession) {
        throw NotFoundError.Workout(uniqueReference);
      }
      oldSession.settings = settings;
      return this.gymStateDb.put(oldSession);
    } catch (error) {
      this.logger.warn(`Error occurred in update workout settings: ${error.message}`);
    }
  }

  public async remove(userWorkoutSession: UserWorkoutSession): Promise<void> {
    const doc = await this.gymStateDb.get(userWorkoutSession.uniqueReference);
    await this.gymStateDb.remove(doc);
    delete (userWorkoutSession as any)._id;
    delete (userWorkoutSession as any)._rev;
  }

  public async getWorkoutSessionFromId(workoutId: string): Promise<UserWorkoutSession | undefined> {
    try {
      const timeBefore = Date.now();
      const dbsession = await this.gymStateDb.get(workoutId);
      this.logger.info(`getWorkoutSessionFromId took ${Date.now() - timeBefore} to complete`);
      return this.assignToUserWorkoutSessionObject(dbsession);
    } catch (err) {
      if (err.status === 404) {
        return undefined;
      }
      throw err;
    }
  }

  public async getFromExternalReferences(
    references: IExternalReference,
  ): Promise<UserWorkoutSession | undefined> {
    const result = await this.gymStateDb.find({
      limit: 1,
      selector: {
        externalReference: {
          $eq: references,
        },
        removedAt: false,
      },
    });

    if (result.docs.length === 0) {
      return;
    }
    return this.assignToUserWorkoutSessionObject(result.docs[0]);
  }

  public async getWorkoutSessionFromIdWithPairedSession(workoutId: string): Promise<UserWorkoutSession | undefined> {
    const workout = await this.getWorkoutSessionFromId(workoutId);
    if (!workout) {
      return undefined;
    }
    if (!workout.pairedSessionReference) {
      return workout;
    }
    const pairedWorkout = await this.getWorkoutSessionFromId(workout.pairedSessionReference);
    workout.pairedSession = pairedWorkout;
    return workout;
  }

  /**
   * Get all the completed workouts for a user, including removed ones that were overlapped.
   */
  public async getWorkoutHistory(userProfileId: string, from: Date): Promise<IUserWorkoutSessionSummary[]> {
    const result = await this.gymStateDb.find({
      limit: 2000,
      selector: {
        endTime: {
          $gte: new Date('2022-01-01'), // avoid very old data with missing fields
          $lt: from,
        },
        userProfileId,
        workoutMarkedAsCompletedAt: {
          $lt: from,
        },
      },

      use_index: process.env.TESTS
        ? undefined
        : [
          '_design/userProfileIdAndEndTimeIndex',
          'userProfileIdAndEndTimeIndex',
        ],
      fields: propNamesIUserWorkoutSessionSummary(),
      sort: [{ endTime: 'desc' }],
    });
    const workouts = result.docs as IUserWorkoutSessionSummary[];

    const completedAndOverlappedWorkouts: IUserWorkoutSessionSummary[] = workouts.filter(
      (workout) => !workout.removedAt || workout.removedReason === RemovedReasonType.Overlapping,
    );
    return completedAndOverlappedWorkouts;
  }

  public async getWorkoutsAfterPointInTime(userProfileId: string, after: Date): Promise<UserWorkoutSession[]> {
    const result = await this.gymStateDb.find({
      limit: Number.MAX_SAFE_INTEGER, // seems like a great idea
      selector: {
        endTime: {
          $gt: after,
        },
        removedAt: false,
        userProfileId,
      },
      use_index: process.env.TESTS
        ? undefined
        : ['_design/userProfileIdAndEndTimeIndex', 'userProfileIdAndEndTimeIndex'],
      fields: [
        '_id',
        '_rev',
        'startTime',
        'startTimeOfFirstExercise',
        'endTime',
        'userProfileId',
        'pairedSessionUserProfileId',
        'settings',
        'uniqueReference',
        'workoutType',
        'gymName',
        'bookingState',
        'pairedSessionReference',
        'expiresAt',
        'removedAt',
        'checkedIn',
        'createdAt',
        'exerciseSessions',
        'openInviteLink',
        'originalEndTime',
        'isDemo',
        'isStreamingWorkout',
        'pairedSessionReference',
        'livestreamOptions',
        'areaCategory',
        'importOrigin',
      ],
      sort: [{ endTime: 'desc' }],
    });
    const sessions: UserWorkoutSession[] = result.docs.map((doc: any): UserWorkoutSession => {
      const workoutSession = this.assignToUserWorkoutSessionObject(doc);
      return workoutSession;
    });
    return sessions;
  }

  public async getLastWorkout(userProfileId: string, from: Date, to: Date): Promise<UserWorkoutSession | undefined> {
    const result = await this.gymStateDb.find({
      limit: 1,
      selector: {
        endTime: {
          $gt: from,
          $lt: to,
        },
        userProfileId,
        removedAt: false,
      },
      use_index: process.env.TESTS ? undefined
        : ['_design/userIdEndTimeAndRemovedAtIndex', 'userIdEndTimeAndRemovedAtIndex'],
      fields: [
        '_id',
        'userProfileId',
        'uniqueReference',
        'endTime',
        'startTime',
        'startTimeOfFirstExercise',
        'removedAt',
        'workoutSelectionId',
        'userRating',
        'gymName',
        'performanceMetrics',
        'workoutType',
        'settings',
        'additionalSecondsBeforeFirstExercise',
        'areaCategory',
      ],
      sort: [{ endTime: 'desc' }],
    });

    if (result.docs.length > 0) {
      return result.docs[0];
    }

    return undefined;
  }

  public async getWorkoutsCurrentlyActiveInStudio(
    userProfileId: string,
    now: Date,
  ): Promise<UserWorkoutSession> {
    const homeGymNames = DbGymRepository.getGymNames(GymTypes.Home);
    const timeBefore = Date.now();
    const result = await this.gymStateDb.find({
      limit: 1,
      selector: {
        userProfileId,
        endTime: {
          $gt: now,
        },
        startTimeOfFirstExercise: {
          $lte: now,
        },
        gymName: {
          $nin: homeGymNames,
        },
        removedAt: false,
        $or: [
          { performanceMetrics: { $exists: false } },
          { performanceMetrics: null },
        ],
      },
    });
    const workouts = result.docs.map((userWorkoutSession: any) => {
      DbWorkoutRepository.buildDates(userWorkoutSession);
      return userWorkoutSession;
    });
    this.logger.info(`getWorkoutsCurrentlyActiveInStudio took ${Date.now() - timeBefore}`);
    return workouts[0];
  }

  public async getWorkoutsForStudioLeaderboard(
    from: Date,
    to: Date,
    gymName: string,
  ): Promise<UserWorkoutSession[]> {
    const result = await this.gymStateDb.find({
      limit: Number.MAX_SAFE_INTEGER,
      selector: {
        endTime: {
          $gte: from,
          $lte: to,
        },
        gymName,
        performanceMetrics: {
          load: {
            $gt: 0,
          },
        },
        removedAt: false,
      },
      use_index: ['_design/endtimeAndRemovedAtIndex', 'endtimeAndRemovedAtIndex'],
    });
    return result.docs.map((userWorkoutSession: any) => {
      DbWorkoutRepository.buildDates(userWorkoutSession);
      return userWorkoutSession;
    });
  }

  public async getWorkoutsWithFirstExerciseOn(startTimeOfFirstExercise: Date): Promise<UserWorkoutSession[]> {
    const result = await this.gymStateDb.find({
      limit: Number.MAX_SAFE_INTEGER,
      selector: {
        startTimeOfFirstExercise,
        removedAt: false,
      },
    });
    return result.docs;
  }

  public async getConfirmedBookingsAndInvitesAndProvisional(
    userId?: string,
    dateOfRequest: Date = new Date(),
  ): Promise<UserWorkoutSession[]> {
    const currentTime = new Date();
    const result = await this.gymStateDb.find({
      selector: {
        endTime: {
          $gte: currentTime,
        },
        $and: [{
          $or: [{
            userProfileId: userId,
          }, {
            pairedSessionUserProfileId: userId,
          }],
        },
        {
          $or: [
            {
              removedAt: false,
            },
            {
              removedAt: {
                $gt: dateOfRequest,
              },
            },
          ],
        }],
      },
      use_index: ['_design/endTimeIndex', 'endTimeIndex'],
      fields: [
        '_id',
        'startTime',
        'startTimeOfFirstExercise',
        'endTime',
        'userProfileId',
        'pairedSessionUserProfileId',
        'settings',
        'uniqueReference',
        'workoutType',
        'gymName',
        'bookingState',
        'pairedSessionReference',
        'expiresAt',
        'removedAt',
        'checkedIn',
        'createdAt',
        'isInstant',
        'openInviteLink',
        'originalEndTime',
        'isDemo',
        'areaCategory',
      ],
    });
    this.logger.info(`got confirmed bookings and invites in ${Date.now() - currentTime.getTime()}`);
    const currentWorkoutSessions = result.docs;
    this.matchWorkoutSessionsWithPairedSessions(currentWorkoutSessions);
    return currentWorkoutSessions.filter((session: UserWorkoutSession) => session.userProfileId === userId);
  }

  public async get(
    gym: Gym,
    userId?: string,
    from: Date = new Date(),
    to?: Date,
    filter: (sessions: UserWorkoutSession[]) => UserWorkoutSession[] = ((s) => s),
    dateOfRequest: Date = new Date(),
  ): Promise<UserWorkoutSession[]> {
    return filter(await this.getAllSessionsForUser(userId, from, to, undefined, gym));
  }

  public async getExpiredAndNotNotifiedAndNotRemovedProvisionalSessions(
    dateToCheckFor: Date,
    filter: (sessions: UserWorkoutSession[]) => UserWorkoutSession[] = ((s) => s),
  ): Promise<UserWorkoutSession[]> {
    const queryOptions: any = {
      limit: 5000,
      selector: {
        removedAt: false,
        expiryNotified: false,
        $and: [
          {
            expiresAt: {
              $exists: true,
            },
          },
          {
            expiresAt: {
              $lte: dateToCheckFor,
            },
          },
        ],
      },
    };
    const sessions = (await this.gymStateDb.find(queryOptions))
      .docs
      .map((session: UserWorkoutSession) => this.assignToUserWorkoutSessionObject(session));
    return filter(sessions);
  }

  public async getHistoryBetweenDates(
    userProfileId: string,
    from: Date,
    to: Date,
  ): Promise<IUserWorkoutSessionSummary[]> {
    const result = await this.gymStateDb.find({
      limit: Number.MAX_SAFE_INTEGER,
      selector: {
        endTime: {
          $lt: to,
          $gt: from,
        },
        userProfileId,
        removedAt: false,
        bookingState: BookingState.ConfirmedBooking,
        checkedIn: true,
      },
      use_index: process.env.TESTS
        ? undefined
        : [
          '_design/userIdEndTimeAndRemovedBookingStateAtIndex',
          'userIdEndTimeAndRemovedBookingStateAtIndex',
        ],
      fields: propNamesIUserWorkoutSessionSummary(),
      sort: [{ endTime: 'desc' }],
    });
    return result.docs;
  }

  public async getNonCheckedInAndNotRemoveWorkoutsStartedBefore(
    time: Date,
    checkInNotified?: boolean,
  ): Promise<UserWorkoutSession[]> {
    const queryOptions: any = {
      selector: {
        startTime: {
          $lte: time,
        },
        removedAt: false,
        checkedIn: false,
      },
    };
    if (checkInNotified !== undefined) {
      queryOptions.selector.checkInNotified = checkInNotified;
    }
    const resultDocs = await this.db
      .queryAndGetDocs(`getNonCheckedInAndNotRemoveWorkoutsStartedBefore(${time}, ${checkInNotified})`, queryOptions);
    const sessions = resultDocs.map((session: UserWorkoutSession) => this.assignToUserWorkoutSessionObject(session));
    Mapper.setupAllAfterDeserialize(sessions);
    return sessions;
  }

  public async getNotCompletedNotRemovedWorkouts(
    time: Date,
  ): Promise<UserWorkoutSession[]> {
    const queryOptions: any = {
      selector: {
        endTime: {
          $lte: time,
        },
        removedAt: false,
        checkedIn: true,
        workoutMarkedAsCompletedAt: false,
      },
      use_index: [
        '_design/workoutMarkedAsCompletedAtRemovedAtCheckedInEndTime',
        'workoutMarkedAsCompletedAtRemovedAtCheckedInEndTime',
      ],
    };
    const resultDocs = await this.db
      .queryAndGetDocs(`getCheckedInAndNotCompletedWorkouts(${time})`, queryOptions);
    const sessions = resultDocs.map((session: UserWorkoutSession) => this.assignToUserWorkoutSessionObject(session));
    Mapper.setupAllAfterDeserialize(sessions);
    return sessions;
  }

  public async getWorkoutSessionToNotifyForReview(now: Date): Promise<UserWorkoutSession[]> {
    const indices = process.env.TESTS
      ? undefined : ['_design/userIdEndTimeAndRemovedAtIndex', 'userIdEndTimeAndRemovedAtIndex'];

    const homeGymNames = DbGymRepository.getGymNames(GymTypes.Home);
    const oneDay = 24 * 60 * 60 * 1000;
    const oneDayBefore = new Date(now.getTime() - oneDay);
    const result = await this.gymStateDb.find({
      limit: 2000,
      selector: {
        checkedIn: true,
        reviewNotified: false,
        endTime: {
          $lt: now,
          $gt: oneDayBefore,
        },
        removedAt: false,
        anonymised: false,
        gymName: {
          $nin: homeGymNames,
        },
      },
      use_index: indices,
      fields: [
        '_id',
        'userProfileId',
        'uniqueReference',
        'userRating',
        'endTime',
        'startTime',
        'startTimeOfFirstExercise',
        'removedAt',
        'workoutSelectionId',
      ],
      sort: [{ endTime: 'desc' }],
    });

    return result.docs;
  }

  public async getNextWorkout(userProfileId: string, after: Date): Promise<NextWorkout | undefined> {
    const result = await this.gymStateDb.find({
      limit: 1,
      selector: {
        endTime: {
          $gt: after,
        },
        removedAt: false,
        $or: [{
          userProfileId,
        }, {
          pairedSessionUserProfileId: userProfileId,
        }],
        bookingState: BookingState.ConfirmedBooking,
      },
      use_index: process.env.TESTS
        ? undefined
        : [
          '_design/userIdEndTimeAndRemovedBookingStateAtIndex',
          'userIdEndTimeAndRemovedBookingStateAtIndex',
        ],
      fields: [
        '_id',
        'earliestAllowedStartTime',
        'startTimeOfFirstExercise',
        'workoutType',
        'settings.cardioOrResistance',
        'settings.threeBodyGroups',
        'settings.level',
        'gymName',
        'startTime',
        'areaCategory',
      ],
      sort: [{ endTime: 'asc' }],
    });
    if (result.docs.length > 0) {
      const nextWorkout = result.docs[0];
      return new NextWorkout(
        nextWorkout._id,
        nextWorkout.earliestAllowedStartTime,
        nextWorkout.startTimeOfFirstExercise,
        nextWorkout.workoutType,
        nextWorkout.settings.cardioOrResistance,
        nextWorkout.settings.threeBodyGroups,
        nextWorkout.settings.level,
        nextWorkout.gymName,
        nextWorkout.startTime,
      );
    }
    return undefined;
  }

  public async getAllPairedWorkouts(workoutId: string, dateOfRequest: Date): Promise<UserWorkoutSession[]> {
    const queryOptions = {
      limit: 1000,
      selector: {
        endTime: {
          $gt: dateOfRequest,
        },
        removedAt: false,
        $and: [{
          $or: [{
            _id: workoutId,
          }, {
            pairedSessionReference: workoutId,
          }],
        }, {
          $or: [{
            bookingState: BookingState.ConfirmedBooking,
          }, {
            bookingState: BookingState.BookingInvite,
          }],
        }],
      },
      use_index: process.env.TESTS
        ? undefined
        : [
          '_design/endtimeAndRemovedAtIndex',
          'endtimeAndRemovedAtIndex',
        ],
      fields: [
        '_id',
        'uniqueReference',
        'userProfileId',
        'workoutType',
        'settings',
        'startTimeOfFirstExercise',
        'bookingState',
      ],
    };

    const logParams = `workoutId: ${workoutId}, dateOfRequest: ${dateOfRequest}`;
    const resultDocs = await this.db.queryAndGetDocs(`getAllPairedWorkouts(${logParams})`, queryOptions);
    return resultDocs;
  }

  public async getWorkoutsForLeaderboard(from: Date): Promise<UserWorkoutSession[]> {
    const homeGymNames = DbGymRepository.getGymNames(GymTypes.Home);
    const queryOptions = {
      limit: 10000,
      selector: {
        // ignore tslint rule that conflicts with eslint rule
        /* tslint:disable object-literal-key-quotes */
        endTime: {
          $gte: from,
        },
        removedAt: false,
        gymName: {
          $in: homeGymNames,
        },
        workoutType: {
          $in: homeWorkoutTypes,
        },
        bookingState: BookingState.ConfirmedBooking,
        performanceMetrics: {
          $exists: true,
        },
        'performanceMetrics.load': {
          $gt: LeaderboardConfig.minProcessedLoad,
        },
      },
      use_index: process.env.TESTS
        ? undefined
        : [
          '_design/removedAtGymNameWorkoutTypeBookingStateIndex',
          'removedAtGymNameWorkoutTypeBookingStateIndex',
        ],
      fields: [
        '_id',
        'uniqueReference',
        'userProfileId',
        'settings',
        'workoutType',
        'performanceMetrics',
      ],
    };

    const logParams = `from: ${from}`;
    const resultDocs = await this.db.queryAndGetDocs(`getWorkoutsForLeaderboard(${logParams})`, queryOptions);
    return resultDocs;
  }

  public async getAllActiveInstructors() {
    const docs = await this.generalSettings.get(HOME_PERSONAL_TRAINERS);
    return docs.instructors
      .filter((pt: Instructor) => pt.isActive)
      .map((pt: Instructor) => pt.name);
  }

  public async getAllSessionsInDateRangeForMembersOfGymInOtherGyms(
    from: Date,
    to: Date,
    gym: Gym,
  ): Promise<IUserWorkoutSessionMemberReport[]> {
    const queryOptions: any = {
      selector: {
        startTime: {
          $gte: from,
          $lte: to,
        },
        memberMainGym: gym.name,
        gymName: {
          $ne: gym.name,
        },
      },
      fields: propNamesIUserWorkoutSessionMemberReport(),
      limit: 20000,
      use_index: [
        '_design/memberMainGymAndStartTimeIndex',
        'memberMainGymAndStartTimeIndex',
      ],
    };

    const logParams = `from: ${from}, to: ${to}`;
    const resultDocs = await this.db.queryAndGetDocs(
      `getAllSessionsInDateRangeForMembersOfGymInOtherGyms(${logParams})`,
      queryOptions,
    );

    const sessions = resultDocs.map((session: UserWorkoutSession) => this.assignToUserWorkoutSessionObject(session));
    return sessions as IUserWorkoutSessionMemberReport[];
  }

  public async getAllSessionsForMembersReportInDateRangeForGym(
    from: Date,
    to: Date,
    gym: Gym,
  ): Promise<IUserWorkoutSessionMemberReport[]> {
    const queryOptions: MangoSelector = {
      selector: {
        startTime: {
          $gte: from,
          $lte: to,
        },
        gymName: gym.name,
      },
      limit: 20000,
      fields: propNamesIUserWorkoutSessionMemberReport(),
      use_index: ['_design/gymNameAndStartTimeIndex', 'gymNameAndStartTimeIndex'],
    };

    const logParams = `from: ${from}, to: ${to}`;

    const resultDocs = await this.db.queryAndGetDocs(
      `getAllSessionsForMembersReportInDateRangeForGym(${logParams})`,
      queryOptions,
    );
    const sessions = resultDocs.map((session: UserWorkoutSession) => this.assignToUserWorkoutSessionObject(session));
    return sessions as IUserWorkoutSessionMemberReport[];
  }

  public async getAllSessionsInDateRangeForGym(
    from: Date,
    to: Date,
    gym: Gym,
  ): Promise<UserWorkoutSession[]> {
    const queryOptions: any = {
      selector: {
        startTime: {
          $gte: from,
          $lte: to,
        },
        gymName: gym.name,
      },
      limit: 100000,
      use_index: ['_design/gymNameAndStartTimeIndex', 'gymNameAndStartTimeIndex'],
    };

    const logParams = `from: ${from}, to: ${to}`;
    const resultDocs = await this.db.queryAndGetDocs(
      `getAllSessionsInDateRangeForGym(${logParams})`,
      queryOptions,
    );

    const sessions = resultDocs.map((session: UserWorkoutSession) => this.assignToUserWorkoutSessionObject(session));
    return sessions;
  }

  /**
   * Full documents are returned
   */
  public async getForUserThatEndedInTimeRange(
    endsFrom: Date,
    endsTo: Date,
    userProfileId: string,
  ): Promise<UserWorkoutSession[]> {
    const queryOptions = {
      selector: {
        endTime: {
          $gt: endsFrom,
          $lt: endsTo,
        },
        userProfileId,
        removedAt: false,
      },
      use_index: ['_design/userIdEndTimeAndRemovedAtIndex', 'userIdEndTimeAndRemovedAtIndex'],
    };
    const resultDocs = await this.db.queryAndGetDocs(
      `getForUserThatEndedInTimeRange for user${userProfileId}`,
      queryOptions,
    );
    const sessions = resultDocs.map((session: UserWorkoutSession) => this.assignToUserWorkoutSessionObject(session));
    return sessions;
  }

  public async getLeaderboardData(
    leaderboardInstance: ILeaderboardInstance,
    showIndividual = true,
    team?: string,
  ): Promise<LeaderboardData> {
    const keys = [
      leaderboardInstance.metric,
      leaderboardInstance.leaderboardId,
      leaderboardInstance.startDate,
      leaderboardInstance.endDate,
    ];

    if (team) {
      keys.push(team);
    }

    const resultFromView = await this.db.queryView('leaderboard/leaderboard-aggregates', {
      startkey: keys,
      endkey: [...keys, {}],
      group_level: 6,
    });
    let mappedResult: LeaderboardData = [];

    if (showIndividual) {
      mappedResult = resultFromView.map((result: any) => {
        const res: IIndividualLeaderboardData = {
          metric: result.key[0],
          leaderboardId: result.key[1],
          startDate: result.key[2],
          endDate: result.key[3],
          userProfileId: result.key[5],
          value: result.value,
        };

        return res;
      });
    } else {
      mappedResult = resultFromView.map((result: any) => {
        const res: IInnerTeamLeaderboardData = {
          metric: result.key[0],
          leaderboardId: result.key[1],
          startDate: result.key[2],
          endDate: result.key[3],
          team: result.key[4],
          userProfileId: result.key[5],
          value: result.value,
        };

        return res;
      });
    }
    mappedResult.sort((a, b) => b.value - a.value);
    return mappedResult;
  }

  public async getLeaderboardConfigInstances(
    leaderboardConfigId: string,
  ): Promise<ILeaderboardInstance[]> {
    const resultFromView = await this.db.queryView('leaderboard/leaderboard-instances', {
      start_key: [leaderboardConfigId],
      end_key: [leaderboardConfigId, {}],
      group_level: 4,
    }) as [{
      key: string[],
      value: number,
    }];

    const mappedResult: ILeaderboardInstance[] = resultFromView.map((result: any) => {
      const mappedItem: ILeaderboardInstance = {
        leaderboardId: result.key[0],
        metric: result.key[1],
        startDate: new Date(result.key[2]).toISOString(),
        endDate: new Date(result.key[3]).toISOString(),
      };
      return mappedItem;
    });
    return mappedResult;
  }

  public async getLeaderboardsForUser(
    userId: string,
    fromDate?: Date,
  ): Promise<IUserLeaderboardInstance[]> {
    const keys = [
      userId,
      ...fromDate ? [fromDate.toISOString()] : [],
    ];

    const resultFromView = await this.db.queryView('leaderboard/user-leaderboards', {
      startkey: keys,
      endkey: [...keys, {}],
      group_level: 4,
    });

    const mappedResult: IUserLeaderboardInstance[] = resultFromView.map((result: any) => {
      const res: IUserLeaderboardInstance = {
        startDate: new Date(result.key[1]),
        endDate: new Date(result.key[2]),
        leaderboardId: result.key[3],
      };

      return res;
    });

    return mappedResult;
  }

  private assignToUserWorkoutSessionObject(dbSession: UserWorkoutSession): UserWorkoutSession {
    if (!dbSession) {
      return dbSession;
    }
    const returnObj = Object.assign(
      new UserWorkoutSession(
        dbSession.userProfileId,
        dbSession.workoutType,
        new WorkoutSettings(dbSession.settings),
      ),
      dbSession,
    );
    DbWorkoutRepository.buildDates(returnObj);

    if (!dbSession.exerciseSessions || dbSession.exerciseSessions.length === 0) {
      returnObj.exerciseSessions = [];
    }
    return returnObj;
  }

  private async getAllSessionsForUser(
    userId?: string,
    from: Date = new Date(),
    to?: Date,
    dateOfRequest?: Date,
    gym?: Gym,
  ): Promise<UserWorkoutSession[]> {
    if (!userId && to && !dateOfRequest) {
      return this.getAllSessionsActiveInDateRangeForAnyUser(from, to, gym);
    }

    const queryOptions: any = {
      selector: {
        endTime: {
          $gte: from,
        },
      },
      limit: 100000,
    };

    if (to) {
      queryOptions.selector = {
        ...queryOptions.selector,
        startTime: {
          $lte: to,
        },
      };
    }

    if (userId) {
      queryOptions.selector = {
        ...queryOptions.selector,
        userProfileId: userId,
      };
    }

    if (dateOfRequest) {
      queryOptions.selector = {
        ...queryOptions.selector,
        removedAt: {
          $gt: dateOfRequest,
        },
      };
    }

    if (gym) {
      queryOptions.selector = {
        ...queryOptions.selector,
        gymName: gym.name,
      };
    }

    if (from && userId) {
      queryOptions.use_index = ['_design/userProfileIdAndEndTimeIndex', 'userProfileIdAndEndTimeIndex'];
    } else if (to && from) {
      queryOptions.use_index = ['_design/endTimeIndex', 'endTimeIndex'];
    }

    const logParams = `userProfileId: ${userId}, from: ${from}, to: ${to}, dateOfRequest: ${dateOfRequest}`;
    const resultDocs = await this.db.queryAndGetDocs(`getAllSessionsForUser(${logParams})`, queryOptions);

    const sessions = resultDocs.map((session: UserWorkoutSession) => this.assignToUserWorkoutSessionObject(session));
    Mapper.setupAllAfterDeserialize(sessions);
    return sessions;
  }

  private async getAllSessionsActiveInDateRangeForAnyUser(
    from: Date,
    to: Date,
    gym?: Gym,
  ): Promise<UserWorkoutSession[]> {
    /*
     * returns sessions that are active in the [from..to] range, ie:
     * - sessions that end at `from` (but not earlier)
     * - sessions that start at `to` (but not later)
     * - sessions between `from` and `to`)
     *
     * since we now a session is at most Xmsec long, we can change the filter to
     * sessions that start X before `from` (at the latest they will end at `from`)
     */
    const latestStartTime = new Date(from.getTime() - this.maxSessionDuration);

    const queryOptions: any = {
      selector: {
        endTime: {
          $gte: from,
        },
        startTime: {
          $gte: latestStartTime,
          $lte: to,
        },
        ...gym ? {
          gymName: gym.name,
        } : {},
      },
      limit: 100000,
      use_index: ['_design/startTimeOnlyIndex', 'startTimeOnlyIndex'],
    };

    const logParams = `from: ${from}, to: ${to}`;
    const resultDocs = await this.db.queryAndGetDocs(
      `getAllSessionsActiveInDateRangeForAnyUser(${logParams})`,
      queryOptions,
    );

    const sessions = resultDocs.map((session: UserWorkoutSession) => this.assignToUserWorkoutSessionObject(session));
    Mapper.setupAllAfterDeserialize(sessions);
    return sessions;
  }

  private matchWorkoutSessionsWithPairedSessions = (currentWorkoutSessions: any[]) => {
    currentWorkoutSessions.forEach((booking, index) => {
      const { pairedSessionReference } = booking;

      if (pairedSessionReference) {
        const inviteBookingIndex = this.getIndexOfPairedWorkoutSession(booking, currentWorkoutSessions);

        if (inviteBookingIndex > -1) {
          const pairedSession = currentWorkoutSessions[inviteBookingIndex];
          currentWorkoutSessions[index] = {
            ...booking,
            pairedSession,
          };
        }
      }
    });
  };

  private groupWorkoutAggregateData(data: [{ key: string[], value: number }]) {
    const aggregatedData: Record<string, number> = {};

    data.forEach((item) => {
      const gymName = item.key[0];
      const userProfileId = item.key[2];
      const aggregateCategory = item.key[3];
      const key = `${gymName},${userProfileId},${aggregateCategory}`;

      if (aggregatedData.hasOwnProperty(key)) {
        aggregatedData[key] += item.value;
      } else {
        aggregatedData[key] = item.value;
      }
    });

    return Object.entries(aggregatedData).map(([key, value]) => {
      const [location, uuid, eventType] = key.split(',');
      return {
        key: [location, uuid, eventType],
        value,
      };
    });
  }

  private getIndexOfPairedWorkoutSession = (
    booking: UserWorkoutSession,
    currentWorkoutSessions: UserWorkoutSession[],
  ) => currentWorkoutSessions
    .findIndex((findBooking: any) => findBooking.pairedSessionReference === booking.uniqueReference);
}
